import { composeEffects } from '@opengov/compose-app/core/module';
import { Map } from 'immutable';
import { createSelector } from '@opengov/compose-app/reselect';
import { reportOgqlView } from '@opengov/api-deprecated';
import { selectUseQueryService } from '@opengov/ogql-client/selectors';
import { createReportConfiguration, IReport } from '@opengov/reporting-types';

import { selectUseLargeScaleQueryLimit } from '../../common/flags';
import { reportLocaleText } from '../../common/locale';
import { reportEffects } from '../../common/report/effects';
import { reportCountQuery } from '../../common/reportConfiguration/effects/queries/reportCountQuery';
import { selectIsInStandardReport } from '../../common/reportConfiguration/selectors';
import { selectCurrentSource } from '../selectors/selectReportExplorerState';
import { reportExplorerActionCreators } from '../state/reportExplorerState';
import { reportActionCreators } from '../../common/report/state';
import { MuiToastTypes } from '../../common/helpers/utils';
import { reportDimensionalEffects } from './reportDimensionalEffects';

export const reportExplorerRouteEffects = composeEffects('report-explorer', (compose) => ({
  getOverallCount: compose.statefulEffect(
    createSelector(
      [reportLocaleText, selectUseLargeScaleQueryLimit, selectIsInStandardReport, selectUseQueryService],
      (reportMsgs, useLargeScaleQueryLimit, isStandardTemplate, useQueryService) => ({
        reportMsgs,
        useLargeScaleQueryLimit,
        isStandardTemplate,
        useQueryService
      })
    ),
    async (
      { reportMsgs, useLargeScaleQueryLimit, isStandardTemplate, useQueryService },
      e: { reportId: string; viewId?: string }
    ) => {
      void reportCountQuery({
        reportConfiguration: createReportConfiguration({
          reportId: e.reportId,
          viewId: e.viewId
        }),
        reportMsgs,
        useLargeScaleQueryLimit,
        isStandardTemplate,
        useQueryService
      });
    }
  ),
  loadReport: compose.statefulEffect(
    createSelector([selectCurrentSource], (currentSource) => ({
      currentSource
    })),
    ({ currentSource }, e: { reportId: string }): Promise<IReport> => {
      return reportEffects
        .getReport({ reportId: e.reportId })
        .then(async (report: IReport) => {
          reportExplorerActionCreators.setAllowedActions(Map(report.allowedActions));
          const sourceId = report.viewId || report.lensId;
          if (currentSource && currentSource.id === sourceId) {
            return report;
          }
          if (report.lensId) {
            //For MDS reports we fetch the lenses when we load the report configuration
            if (!report.isMultiDataset) {
              await reportDimensionalEffects.getReportDatasetLenses({ reportId: e.reportId });
            }
            return report;
          }
          return reportOgqlView({ reportId: report.id }).then((view) => {
            reportExplorerActionCreators.receivedReportView(view);
            return report;
          });
        })
        .catch((err) => {
          if (err.status !== 404) {
            reportActionCreators.toggleMuiToast({
              type: MuiToastTypes.FAILURE,
              headerText: 'Load Report Failed',
              bodyText: 'We were unable to load your report. Please try again.',
              dataTest: `toast-load-report-failure`
            });
          }
          throw err;
        });
    }
  )
}));
